import { NavLink, useNavigate, useLoaderData } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createListCollection, Box, Button, Input, Stack, SimpleGrid } from '@chakra-ui/react';
import AuthHeader from '@/components/auth-header';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { SportType } from '@/lib/types';

const formSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    workspaceName: z.string().min(1, 'Workspace name is required'),
    sportTypeId: z
      .string({ required_error: 'Sport is required' })
      .min(1, 'Sport is required')
      .array(),
    email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof formSchema>;

const Register = () => {
  const sportTypes = useLoaderData() as SportType[];
  const sortedSportTypes = [...sportTypes].sort((a: SportType, b: SportType) =>
    a.name.localeCompare(b.name),
  );
  const sports = createListCollection({
    items: sortedSportTypes,
    itemToString: (item) => item.name,
    itemToValue: (item) => item.id + '',
  });
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.post('auth/signup', {
        first_name: data.firstName,
        last_name: data.lastName,
        workspace_name: data.workspaceName,
        sport_type_id: data.sportTypeId.length > 0 ? parseInt(data.sportTypeId[0], 10) : null,
        email: data.email,
        password: data.password,
        c_password: data.confirmPassword,
      });

      toaster.create({
        type: 'success',
        title: 'You have received an account confirmation email!',
        description: 'Please open this email and confirm your account email address to continue.',
      });

      navigate('/auth/login');
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to register.',
      });
    }
  };

  return (
    <Stack gap={6} pb={8}>
      <AuthHeader title='Create account' />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} width={'full'} gap={4}>
            <Field
              label='First name'
              invalid={!!errors.firstName}
              errorText={errors.firstName?.message}
            >
              <Input {...register('firstName')} />
            </Field>
            <Field
              label='Last name'
              invalid={!!errors.lastName}
              errorText={errors.lastName?.message}
            >
              <Input {...register('lastName')} />
            </Field>
          </SimpleGrid>
          <Field
            label='Workspace name'
            invalid={!!errors.workspaceName}
            errorText={errors.workspaceName?.message}
          >
            <Input {...register('workspaceName')} />
          </Field>
          <Field
            label='Choose your sport'
            invalid={!!errors.sportTypeId}
            errorText={errors.sportTypeId?.message}
          >
            <Controller
              control={control}
              name='sportTypeId'
              render={({ field }) => (
                <SelectRoot
                  collection={sports}
                  name={field.name}
                  value={field.value}
                  onValueChange={({ value }) => field.onChange(value)}
                  onInteractOutside={() => field.onBlur()}
                >
                  <SelectTrigger>
                    <SelectValueText placeholder='Select sport' />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedSportTypes.map((sportType) => (
                      <SelectItem key={sportType.id} item={sportType}>
                        {sportType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            />
          </Field>
          <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
            <Input {...register('email')} type='email' />
          </Field>
          <SimpleGrid columns={{ base: 1, md: 2 }} width={'full'} gap={4}>
            <Field
              label='Password'
              invalid={!!errors.password}
              errorText={errors.password?.message}
            >
              <Input {...register('password')} type='password' />
            </Field>
            <Field
              label='Confirm password'
              invalid={!!errors.confirmPassword}
              errorText={errors.confirmPassword?.message}
            >
              <Input {...register('confirmPassword')} type='password' />
            </Field>
          </SimpleGrid>
          <Button type={'submit'} width={'full'} loading={isSubmitting} loadingText='Loading...'>
            Create account
          </Button>
        </Stack>
      </form>
      <Box textAlign={'center'}>
        <Button fontSize={'sm'} variant={'ghost'} asChild>
          <NavLink to='/auth/login'>Already have an account?</NavLink>
        </Button>
      </Box>
    </Stack>
  );
};

export default Register;
